import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const GaugeChart = ({ value, label, color }: { value: number; label: string; color: string }) => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const data = [
    { name: 'value', value: value },
    { name: 'remaining', value: 100 - value }
  ];

  // Return a simple placeholder while not mounted to avoid Recharts context issues
  if (!isMounted) {
    return (
      <div className="w-1/2 h-[100px] relative">
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-xl font-bold text-brand-primary font-space-grotesk">
            {value}{label === 'Accuracy' ? '%' : ''}
          </span>
          <span className="text-xs text-muted-foreground">
            {label === 'Accuracy' ? 'Accuracy' : 'Trans/Hour'}
          </span>
        </div>
      </div>
    );
  }

  try {
    return (
      <div className="w-1/2 h-[100px] relative">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              startAngle={180}
              endAngle={0}
              innerRadius={25}
              outerRadius={45}
              dataKey="value"
            >
              <Cell fill={color} />
              <Cell fill="hsl(var(--brand-secondary))" />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <span className="text-xl font-bold text-brand-primary font-space-grotesk">
            {value}{label === 'Accuracy' ? '%' : ''}
          </span>
          <span className="text-xs text-muted-foreground">
            {label === 'Accuracy' ? 'Accuracy' : 'Trans/Hour'}
          </span>
        </div>
      </div>
    );
  } catch (error) {
    console.error('Chart rendering error:', error);
    // Fallback to simple display if chart fails
    return (
      <div className="w-1/2 h-[100px] relative flex flex-col items-center justify-center bg-brand-secondary rounded-full">
        <span className="text-xl font-bold text-brand-primary font-space-grotesk">
          {value}{label === 'Accuracy' ? '%' : ''}
        </span>
        <span className="text-xs text-muted-foreground">
          {label === 'Accuracy' ? 'Accuracy' : 'Trans/Hour'}
        </span>
      </div>
    );
  }
};

export const PerformanceGauges = () => {
  // Calculate real-time accuracy from transactions
  const { data: accuracyData } = useQuery({
    queryKey: ['performance-accuracy'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('transactions')
        .select('ai_classification_confidence, human_verified')
        .eq('human_verified', true);

      if (error) throw error;

      if (!data || data.length === 0) return 0;

      const avgConfidence = data.reduce((sum, t) => sum + (t.ai_classification_confidence || 0), 0) / data.length;
      return Math.round(avgConfidence);
    }
  });

  // Calculate processing speed (transactions per hour in last 24h)
  const { data: speedData } = useQuery({
    queryKey: ['performance-speed'],
    queryFn: async () => {
      const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

      const { data, error } = await supabase
        .from('transactions')
        .select('created_at')
        .gte('created_at', oneDayAgo);

      if (error) throw error;

      if (!data || data.length === 0) return 0;

      // Calculate transactions per hour
      const transactionsPerHour = Math.round(data.length / 24);
      // Normalize to 0-100 scale (assume 100 trans/hour is maximum)
      return Math.min(Math.round((transactionsPerHour / 100) * 100), 100);
    }
  });

  return (
    <>
      <GaugeChart
        value={accuracyData || 0}
        label="Accuracy"
        color="hsl(var(--brand-primary))"
      />
      <GaugeChart
        value={speedData || 0}
        label="Speed"
        color="hsl(var(--brand-accent))"
      />
    </>
  );
};